/**
 * Groups page — configured only by permissions (page_configuration).
 * Requires explicit configuration.view: 'organization' or 'team'. No default; empty config shows "View not configured".
 *
 * - view = 'organization': lands on "My Groups" in the current org; System (non-Guest) can open all organizations.
 * - view = 'team': teams scoped to current org; System context at System org can see all teams.
 *
 * Create-group / add-period UI and role templates (Basic, future Super Admin) live in GroupsOrganizationView.
 * Groups page_configuration may include `can_open_member_profiles` (member rows link to User Profile; default off),
 * `can_view_group_members` / `can_view_member_details` (roster and email visibility; opt-in, default off),
 * `can_view_member_access` (UGP window, override_period, is_active, can_login, enrollment badges; opt-in).
 * @see docs/group-role-templates.md
 */
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePageConfig } from '../contexts/PageConfigContext';
import GroupsOrganizationView from './GroupsOrganizationView';
import {
  getGroupsPageCapabilities,
  resolveTeamScopeParentId
} from '../utils/groupsPage';
import { viewerIsPlatformOwner } from '../utils/systemOwner';
import './Groups.css';

const VIEW_NOT_CONFIGURED_MESSAGE = 'View not configured. Reload if temporary error suspected. If issue continues, contact support.';

const Groups = () => {
  const { user, availableUgp, userGroupsPeriods, currentRole } = useAuth();
  const pageConfig = usePageConfig();
  const view = pageConfig?.configuration?.view;
  const { canCreateEntities, canAddMembers } = getGroupsPageCapabilities(pageConfig?.configuration);
  const platformOwnerViewer = viewerIsPlatformOwner({
    userId: user?.id,
    currentRole,
    enrollments: availableUgp
  });

  // Require explicit view: no default when configuration is empty or view missing
  const normalizedView = view === 'role' ? 'team' : view;
  if (normalizedView !== 'organization' && normalizedView !== 'team') {
    return (
      <div className="groups-view-not-configured">
        <p>{VIEW_NOT_CONFIGURED_MESSAGE}</p>
      </div>
    );
  }

  const teamGroupNameIncludes =
    pageConfig?.configuration?.team_group_name_includes ||
    pageConfig?.configuration?.role_group_name_includes;

  if (normalizedView === 'organization') {
    return (
      <GroupsOrganizationView
        groupType="organization"
        canCreateEntities={canCreateEntities}
        canAddMembers={canAddMembers}
        platformOwnerViewer={platformOwnerViewer}
      />
    );
  }

  const scopeParentId = resolveTeamScopeParentId(availableUgp, userGroupsPeriods, currentRole);
  return (
    <GroupsOrganizationView
      groupType="organization"
      scopeParentId={scopeParentId}
      teamGroupNameIncludes={teamGroupNameIncludes}
      restrictNestedGroupsToUserAccess
      canCreateEntities={canCreateEntities}
      canAddMembers={canAddMembers}
      platformOwnerViewer={platformOwnerViewer}
    />
  );
};

export default Groups;
